import { useEffect, useMemo, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Check, X } from "lucide-react";

export const ALLERGY_OPTIONS = [
  "Cow's milk",
  "Eggs",
  "Peanuts",
  "Tree nuts",
  "Sesame",
  "Wheat",
  "Soy",
  "Fish",
  "Shellfish",
  "Other",
];

/**
 * Multi-select allergies picker with an "Other" free-text field.
 * Stores the value as a comma-separated string for backend compatibility,
 * e.g. "Peanuts, Tree nuts, Other: kiwi".
 */
export function AllergiesField({ value, onChange, testIdPrefix = "allergies" }) {
  const initial = useMemo(() => parseAllergies(value), [value]);
  const [selected, setSelected] = useState(initial.selected);
  const [otherText, setOtherText] = useState(initial.otherText);
  const [open, setOpen] = useState(false);

  // Re-parse when the upstream value changes (e.g. after fetch)
  useEffect(() => {
    const next = parseAllergies(value);
    setSelected(next.selected);
    setOtherText(next.otherText);
  }, [value]);

  const propagate = (nextSelected, nextOtherText) => {
    onChange(serializeAllergies(nextSelected, nextOtherText));
  };

  const toggle = (opt) => {
    const has = selected.includes(opt);
    const next = has ? selected.filter((o) => o !== opt) : [...selected, opt];
    setSelected(next);
    if (opt === "Other" && has) {
      setOtherText("");
      propagate(next, "");
    } else {
      propagate(next, otherText);
    }
  };

  const summary = selected.length === 0 ? "No allergies selected" : selected.join(", ");

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-field`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left h-auto min-h-10 rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center justify-between gap-2"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <span className={selected.length === 0 ? "text-muted-foreground" : ""}>{summary}</span>
        <span className="text-xs text-muted-foreground">{open ? "Hide" : "Edit"}</span>
      </button>

      {open && (
        <div className="border border-border rounded-md p-3 space-y-2" data-testid={`${testIdPrefix}-options`}>
          <div className="flex flex-wrap gap-2">
            {ALLERGY_OPTIONS.map((opt) => {
              const active = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => toggle(opt)}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input hover:border-primary/50"}`}
                  data-testid={`${testIdPrefix}-opt-${opt.replace(/[^a-z0-9]/gi, "_").toLowerCase()}`}
                >
                  {active ? <Check size={12} /> : null}
                  {opt}
                </button>
              );
            })}
          </div>
          {selected.includes("Other") && (
            <div className="space-y-1 pt-2">
              <label className="text-xs text-muted-foreground">Please specify "Other" allergies</label>
              <Input
                value={otherText}
                onChange={(e) => { setOtherText(e.target.value); propagate(selected, e.target.value); }}
                placeholder="e.g. kiwi, mustard"
                data-testid={`${testIdPrefix}-other-input`}
              />
            </div>
          )}
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => { setSelected([]); setOtherText(""); propagate([], ""); }}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
              data-testid={`${testIdPrefix}-clear`}
            >
              <X size={12} /> Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Parse "Peanuts, Tree nuts, Other: kiwi, mustard" -> { selected: [...], otherText: "kiwi, mustard" }
function parseAllergies(raw) {
  if (!raw) return { selected: [], otherText: "" };
  const parts = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
  const selected = [];
  let otherText = "";
  for (const p of parts) {
    const m = p.match(/^other\s*:\s*(.+)$/i);
    if (m) {
      otherText = (otherText ? otherText + ", " : "") + m[1].trim();
      if (!selected.includes("Other")) selected.push("Other");
      continue;
    }
    const matched = ALLERGY_OPTIONS.find((o) => o.toLowerCase() === p.toLowerCase());
    if (matched) {
      if (!selected.includes(matched)) selected.push(matched);
    } else {
      // unknown free-text value -> bucket into Other
      otherText = (otherText ? otherText + ", " : "") + p;
      if (!selected.includes("Other")) selected.push("Other");
    }
  }
  return { selected, otherText };
}

function serializeAllergies(selected, otherText) {
  const out = [];
  for (const s of selected) {
    if (s === "Other") {
      if (otherText && otherText.trim()) out.push(`Other: ${otherText.trim()}`);
    } else {
      out.push(s);
    }
  }
  return out.join(", ");
}

export default AllergiesField;
